import React, { useState, useEffect, useContext } from "react";
import { Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import {
  IconBuilding,
  IconLink,
  IconWorld,
  IconCheck,
  IconAlertCircle,
  IconSparkles,
  IconRocket,
  IconUsers,
  IconBrush,
  IconArrowRight,
  IconInfoCircle,
  IconCopy,
  IconArrowLeft,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import OrganizationService from "../services/OrganizationServices";
import UserContext from "../../../contexts/UserContext";
import "../css/createOrganization.css";

const CreateOrganization = ({ isModal = false, onClose, onSuccess }) => {
  const { getUserPlan } = useContext(UserContext);
  
  // Verificar se o usuário tem acesso a domínio personalizado
  const hasCustomDomainAccess = () => {
    return [2, 5, 9, 10, 11].includes(getUserPlan());
  };

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: "",
    brand_name: "",
    description: "",
    custom_domain: "",
  });

  // Estados de controle
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [domainChecking, setDomainChecking] = useState(false);
  const [domainAvailable, setDomainAvailable] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  // Verificar disponibilidade do domínio com debounce
  useEffect(() => {
    if (formData.custom_domain && formData.custom_domain.length >= 3) {
      const timeoutId = setTimeout(async () => {
        setDomainChecking(true);
        try {
          await OrganizationService.checkDomainAvailability(
            formData.custom_domain
          );
          setDomainAvailable(true);
        } catch (error) {
          setDomainAvailable(false);
        } finally {
          setDomainChecking(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [formData.custom_domain]);

  // Manipular mudanças no formulário
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Limpar erro específico do campo
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Avançar para próximo step
  const handleNextStep = () => {
    const validation = OrganizationService.validateOrganizationData(formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }


    if (hasCustomDomainAccess() && formData.custom_domain && domainAvailable === false) {
      setErrors({ custom_domain: "Este domínio já está em uso" });
      return;
    }

    setCurrentStep(2);
    setErrors({});
  };

  // Voltar para step anterior
  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  // Submeter formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (currentStep === 1) {
      handleNextStep();
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Preparar dados para envio - remover custom_domain para planos 3 e 4 ou se estiver vazio
      const dataToSend = { ...formData };
      if ([3, 4].includes(getUserPlan()) || !formData.custom_domain.trim()) {
        delete dataToSend.custom_domain;
      }
      
      const response = await OrganizationService.createOrganization(dataToSend);

      // Mostrar sucesso com SweetAlert - Mensagem diferente para planos 3 e 4
      const isBasicPlan = [3, 4].includes(getUserPlan());
      const successMessage = isBasicPlan
        ? "Sua organização foi criada com sucesso! Agora você pode criar agentes para empresas de acordo com seu plano."
        : "Sua organização white label foi criada com sucesso! Agora você pode personalizar as cores, logo e convidar empresas.";

      await Swal.fire({
        icon: "success",
        title: "🎉 Organização Criada!",
        html: `
          <div style="text-align: left; padding: 20px;">
            <p><strong>Nome:</strong> ${response.name}</p>
            ${!isBasicPlan ? `<p><strong>URL:</strong> <a href="${response.domain_url}" target="_blank">${response.domain_url}</a></p>` : ''}
            <br/>
            <p style="color: #6c757d; font-size: 14px;">
              ${successMessage}
            </p>
          </div>
        `,
        confirmButtonText: "Continuar",
        confirmButtonColor: "#3b82f6",
      });

      // Chamar callback de sucesso se fornecido
      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      console.error("Erro ao criar organização:", error);

      if (error.response?.data) {
        const serverErrors = error.response.data;
        setErrors(serverErrors);

        // Mostrar erro específico
        const errorMessage = Object.values(serverErrors)[0];
        if (typeof errorMessage === "string") {
          Swal.fire({
            icon: "error",
            title: "Erro ao criar organização",
            text: errorMessage,
            confirmButtonColor: "#3b82f6",
          });
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro inesperado",
          text: "Ocorreu um erro ao criar a organização. Tente novamente.",
          confirmButtonColor: "#3b82f6",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Copiar URL para clipboard
  const copyUrlToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    Swal.fire({
      icon: "success",
      title: "Copiado!",
      text: "URL copiada para a área de transferência",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  // Preview da URL
  const previewUrl = OrganizationService.previewOrganizationUrl(
    formData.custom_domain
  );

  const containerClass = isModal
    ? "create-organization-container create-organization-container--modal"
    : "create-organization-container";

  return (
    <div className={containerClass}>
      <div className="create-organization-container__wrapper">
        {!isModal && (
          <>
            {/* Header - apenas se não for modal */}
            <div className="create-organization-container__header">
              <div className="create-organization-container__header-content">
                <div className="create-organization-container__header-icon">
                  <IconSparkles size={32} />
                </div>
                <div>
                  <h1 className="create-organization-container__title">
                    Criar Organização White Label
                  </h1>
                  <p className="create-organization-container__subtitle">
                    Crie sua própria plataforma personalizada em poucos minutos
                  </p>
                </div>
              </div>

              {/* Progress Steps */}
              <div className="create-organization-container__progress">
                <div
                  className={`create-organization-container__step ${
                    currentStep >= 1
                      ? "create-organization-container__step--active"
                      : ""
                  }`}
                >
                  <div className="create-organization-container__step-number">
                    1
                  </div>
                  <span className="create-organization-container__step-label">
                    Informações Básicas
                  </span>
                </div>
                <div className="create-organization-container__step-line"></div>
                <div
                  className={`create-organization-container__step ${
                    currentStep >= 2
                      ? "create-organization-container__step--active"
                      : ""
                  }`}
                >
                  <div className="create-organization-container__step-number">
                    2
                  </div>
                  <span className="create-organization-container__step-label">
                    Revisão e Criação
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        <Row className="justify-content-center">
          <Col xs={12}>
            <Card className="create-organization-container__card">
              <Card.Body className="create-organization-container__card-body">
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 && (
                    <>
                      {/* Step 1: Informações Básicas */}
                      <div className="create-organization-container__step-content">
                        <div className="create-organization-container__step-header">
                          <IconBuilding
                            size={24}
                            className="create-organization-container__step-icon"
                          />
                          <h3>Informações da Organização</h3>
                          <p>
                            Defina as informações básicas da sua organização
                          </p>
                        </div>

                        {/* Nome da Organização */}
                        <div className="create-organization-container__form-group">
                          <label className="create-organization-container__form-label create-organization-container__form-label--required">
                            <IconBuilding size={16} />
                            Nome da organização
                          </label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Ex: Minha Agência Digital"
                            className={`create-organization-container__form-control ${
                              errors.name ? "is-invalid" : ""
                            }`}
                            maxLength={150}
                          />
                          {errors.name && (
                            <div className="create-organization-container__invalid-feedback">
                              {errors.name}
                            </div>
                          )}
                          <small className="create-organization-container__form-text">
                            Este será o nome principal da sua organização
                          </small>
                        </div>

                        {/* Nome da Marca */}
                        <div className="create-organization-container__form-group">
                          <label className="create-organization-container__form-label create-organization-container__form-label--required">
                            <IconBrush size={16} />
                            Nome da marca
                          </label>
                          <Form.Control
                            type="text"
                            name="brand_name"
                            value={formData.brand_name}
                            onChange={handleInputChange}
                            placeholder="Ex: Agência Digital Pro"
                            className={`create-organization-container__form-control ${
                              errors.brand_name ? "is-invalid" : ""
                            }`}
                            maxLength={100}
                          />
                          {errors.brand_name && (
                            <div className="create-organization-container__invalid-feedback">
                              {errors.brand_name}
                            </div>
                          )}
                          <small className="create-organization-container__form-text">
                            Este nome será utilizado em emails, notificações e outras partes do sistema que representam sua marca
                          </small>
                        </div>

                        {/* Descrição */}
                        <div className="create-organization-container__form-group">
                          <label className="create-organization-container__form-label">
                            <IconInfoCircle size={16} />
                            Descrição (Opcional)
                          </label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Descreva sua organização e o que ela oferece..."
                            className="create-organization-container__form-control"
                            maxLength={500}
                          />
                          <small className="create-organization-container__form-text">
                            {formData.description.length}/500 caracteres
                          </small>
                        </div>

                        {/* Domínio Customizado - Apenas para planos 2 ou 5 */}
                        {hasCustomDomainAccess() && (
                          <div className="create-organization-container__form-group">
                            <label className="create-organization-container__form-label">
                              <IconWorld size={16} />
                              Domínio Personalizado
                            </label>
                            <div className="create-organization-container__input-with-status">
                              <Form.Control
                                type="text"
                                name="custom_domain"
                                value={formData.custom_domain}
                                onChange={handleInputChange}
                                placeholder="minhaagencia.com"
                                className={`create-organization-container__form-control ${
                                  errors.custom_domain
                                    ? "is-invalid"
                                    : domainAvailable === true
                                    ? "is-valid"
                                    : ""
                                }`}
                              />
                              {domainChecking && (
                                <div className="create-organization-container__input-status create-organization-container__input-status--checking">
                                  <Spinner size="sm" />
                                </div>
                              )}
                              {!domainChecking && domainAvailable === true && (
                                <div className="create-organization-container__input-status create-organization-container__input-status--available">
                                  <IconCheck size={16} />
                                </div>
                              )}
                              {!domainChecking && domainAvailable === false && (
                                <div className="create-organization-container__input-status create-organization-container__input-status--unavailable">
                                  <IconAlertCircle size={16} />
                                </div>
                              )}
                            </div>
                            {errors.custom_domain && (
                              <div className="create-organization-container__invalid-feedback">
                                {errors.custom_domain}
                              </div>
                            )}
                            {console.log(errors)}
                            {!errors.custom_domain &&
                              domainAvailable === false && (
                                <div className="create-organization-container__invalid-feedback">
                                  Este domínio já está em uso
                                </div>
                              )}
                            {!errors.custom_domain &&
                              domainAvailable === true && (
                                <div className="create-organization-container__valid-feedback">
                                  Domínio disponível!
                                </div>
                              )}
                            <small className="create-organization-container__form-text">
                              Você precisará configurar o DNS para apontar para
                              nossos servidores
                            </small>
                          </div>
                        )}

                        {/* Botões */}
                        <div className="create-organization-container__form-actions">
                          {isModal && (
                            <Button
                              variant="outline-secondary"
                              size="lg"
                              onClick={onClose}
                              className="create-organization-container__btn-cancel"
                            >
                              Cancelar
                            </Button>
                          )}
                          <Button
                            variant="primary"
                            size="lg"
                            type="submit"
                            className="create-organization-container__btn-next"
                            disabled={
                              !formData.name ||
                              !formData.brand_name ||
                              domainChecking
                            }
                          >
                            Continuar
                            <IconArrowRight size={16} />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {currentStep === 2 && (
                    <>
                      {/* Step 2: Revisão */}
                      <div className="create-organization-container__step-content">
                        <div className="create-organization-container__step-header">
                          <IconCheck
                            size={24}
                            className="create-organization-container__step-icon"
                          />
                          <h3>Revisão e Confirmação</h3>
                          <p>
                            Confira os dados da sua organização antes de criar
                          </p>
                        </div>

                        {/* Preview da Organização */}
                        <div className="create-organization-container__preview">
                          <div className="create-organization-container__preview-header">
                            <IconBuilding size={20} />
                            <h4>Preview da Organização</h4>
                          </div>

                          <div className="create-organization-container__preview-content">
                            <div className="create-organization-container__preview-item">
                              <label>Organização:</label>
                              <span>{formData.name}</span>
                            </div>

                            <div className="create-organization-container__preview-item">
                              <label>Nome da Marca:</label>
                              <span>{formData.brand_name}</span>
                            </div>

                            {formData.description && (
                              <div className="create-organization-container__preview-item">
                                <label>Descrição:</label>
                                <span>{formData.description}</span>
                              </div>
                            )}
                            {/* URL Principal - Ocultar para planos 3 e 4 */}
                            {![3, 4].includes(getUserPlan()) && (
                              <div className="create-organization-container__preview-item">
                                <label>URL Principal:</label>
                                <div className="create-organization-container__url-preview">
                                  <span>{previewUrl}</span>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => copyUrlToClipboard(previewUrl)}
                                    className="create-organization-container__copy-btn"
                                  >
                                    <IconCopy size={14} />
                                  </Button>
                                </div>
                              </div>
                            )}
                            {hasCustomDomainAccess() && formData.custom_domain && (
                              <div className="create-organization-container__preview-item">
                                <label>Domínio Personalizado:</label>
                                <span>https://{formData.custom_domain}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Próximos Passos */}
                        <div className="create-organization-container__next-steps">
                          <h5 className="create-organization-container__next-steps-title">
                            <IconRocket size={18} />O que vem depois?
                          </h5>
                          <div className="create-organization-container__next-steps-list">
                            {/* Personalização - Ocultar para planos 3 e 4 */}
                            {![3, 4].includes(getUserPlan()) && (
                              <div className="create-organization-container__next-step-item">
                                <IconBrush size={16} />
                                <div>
                                  <strong>Personalização</strong>
                                  <small>
                                    Configure cores, logo e favicon da sua marca
                                  </small>
                                </div>
                              </div>
                            )}
                            <div className="create-organization-container__next-step-item">
                              <IconUsers size={16} />
                              <div>
                                <strong>Criar conta cliente</strong>
                                <small>
                                  Crie contas cliente para utilizarem sua plataforma
                                </small>
                              </div>
                            </div>
                            {/* Configurar Domínio - Ocultar para planos 3 e 4 */}
                            {![3, 4].includes(getUserPlan()) && (
                              <div className="create-organization-container__next-step-item">
                                <IconWorld size={16} />
                                <div>
                                  <strong>Configurar Domínio</strong>
                                  <small>
                                    Configure o DNS se você escolheu um domínio
                                    personalizado
                                  </small>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Botões */}
                        <div className="create-organization-container__form-actions">
                          <Button
                            variant="outline-secondary"
                            size="lg"
                            onClick={handlePreviousStep}
                            disabled={loading}
                            className="create-organization-container__btn-back"
                          >
                            <IconArrowLeft size={16} />
                            Voltar
                          </Button>
                          <Button
                            variant="primary"
                            size="lg"
                            type="submit"
                            disabled={loading}
                            className="create-organization-container__btn-create"
                          >
                            {loading ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Criando...
                              </>
                            ) : (
                              <>
                                <IconSparkles size={16} />
                                Criar Organização
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Benefits Section - apenas se não for modal */}
        {!isModal && (
          <Row className="justify-content-center mt-5">
            <Col xs={12} lg={10}>
              <div className="create-organization-container__benefits">
                <h3 className="create-organization-container__benefits-title">
                  <IconSparkles size={24} />
                  Vantagens da Organização White Label
                </h3>
                <Row>
                  <Col xs={12} md={4} className="mb-4">
                    <div className="create-organization-container__benefit-card">
                      <div className="create-organization-container__benefit-icon">
                        <IconBrush size={24} />
                      </div>
                      <h4>Marca Própria</h4>
                      <p>
                        Personalize cores, logo e domínio com sua identidade
                        visual
                      </p>
                    </div>
                  </Col>
                  <Col xs={12} md={4} className="mb-4">
                    <div className="create-organization-container__benefit-card">
                      <div className="create-organization-container__benefit-icon">
                        <IconUsers size={24} />
                      </div>
                      <h4>Múltiplas Empresas</h4>
                      <p>
                        Convide até 10 empresas para usar sua plataforma
                        personalizada
                      </p>
                    </div>
                  </Col>
                  <Col xs={12} md={4} className="mb-4">
                    <div className="create-organization-container__benefit-card">
                      <div className="create-organization-container__benefit-icon">
                        <IconRocket size={24} />
                      </div>
                      <h4>Fácil Gestão</h4>
                      <p>
                        Gerencie todas as empresas em um painel administrativo
                        intuitivo
                      </p>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default CreateOrganization;
